import ReactEcharts from 'echarts-for-react'

import { ContentCard } from '@/components/customComponents'

import {
  barChartOptions,
  lineChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import { useSpend } from '@/hooks'
import { useReduxBaseFilter } from '@/store'
import { SeasonalityModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'

interface SpendTabProps {
  itemName: string
}

export const SpendTab = ({ itemName }: SpendTabProps) => {
  // Get filter state from Redux
  const { selectedBranch, date } = useReduxBaseFilter()

  const {
    supplierBreakdownChartData,
    storeBreakdownChartData,
    seasonalityChartData,
    loading,
  } = useSpend(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* TODO: product name */}
        <ContentCard title="Supplier Breakdown" loading={loading}>
          {supplierBreakdownChartData &&
            supplierBreakdownChartData.rawData.length > 0 && (
              <ReactEcharts
                option={getStackedBarChartData({
                  rawData: supplierBreakdownChartData.rawData,
                  seriesNames: supplierBreakdownChartData.seriesNames,
                  xAxisNames: supplierBreakdownChartData.xAxisNames,
                })}
              />
            )}
        </ContentCard>
        <ContentCard title="Store Breakdown" loading={loading}>
          {storeBreakdownChartData && (
            <ReactEcharts
              option={getBarChartData(storeBreakdownChartData.data)}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <ContentCard title="Seasonality" loading={loading}>
          {seasonalityChartData && (
            <ReactEcharts
              option={getLineChartData(seasonalityChartData.data)}
            />
          )}
        </ContentCard>
      </div>
    </div>
  )
}

const getStackedBarChartData = (primaryData: {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}) => {
  return stackedBarChartOptions(
    primaryData.rawData,
    primaryData.seriesNames,
    primaryData.xAxisNames,
    '40%',
    'money'
  )
}

const getBarChartData = (
  storeData: { branchName: string; grossSalesAmount: number }[]
) => {
  const rawData = [storeData.map((item) => item.grossSalesAmount)]
  const seriesNames = ['Spend']
  const xAxisNames = storeData.map((item) => item.branchName)

  return barChartOptions(rawData, seriesNames, xAxisNames, 'money')
}

const getLineChartData = (seasonalityData: SeasonalityModel) => {
  // Safety check for empty data
  if (!seasonalityData || Object.keys(seasonalityData).length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  const periods = Object.keys(seasonalityData) // These will be our line names/series
  const allMonths = new Set<string>()

  // Collect all unique months (these will be our X-axis data points)
  periods.forEach((period) => {
    if (seasonalityData[period] && Array.isArray(seasonalityData[period])) {
      seasonalityData[period].forEach((item) => {
        if (item && item.month) {
          allMonths.add(item.month)
        }
      })
    }
  })

  const monthNames = Array.from(allMonths) // Months should be in their natural order

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  // Create raw data for each period (line)
  const rawData = periods.map((period) => {
    return monthNames.map((month) => {
      const monthData = seasonalityData[period]?.find(
        (item) => item?.month === month
      )
      return monthData ? monthData.grossSalesAmount : 0
    })
  })

  return lineChartOptions(rawData, periods, monthNames, 'money')
}
